import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import { Card, CardBody } from "../components/ui/Card";
import { useAuth } from "../hooks/useAuth";
import {
  fetchAllCalendarDays,
  createCalendarDay,
  upsertCalendarDay,
  deleteCalendarDay,
} from "../services/liturgical";
import { fetchAllSaints } from "../services/saints";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function monthName(m0) {
  return new Date(2000, m0, 1).toLocaleString(undefined, { month: "long" });
}

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

function startWeekday(year, monthIndex0) {
  return new Date(year, monthIndex0, 1).getDay();
}

function dateIdFromParts(year, month0, day) {
  return `${pad2(day)}-${pad2(month0 + 1)}-${year}`;
}

function dateIdToISO(id) {
  const [dd = "", mm = "", yyyy = ""] = String(id || "").split("-");
  if (!dd || !mm || !yyyy) return "";
  return `${yyyy}-${mm}-${dd}`;
}

function isoToDateId(iso) {
  const [yyyy = "", mm = "", dd = ""] = String(iso || "").split("-");
  if (!dd || !mm || !yyyy) return "";
  return `${dd}-${mm}-${yyyy}`;
}

function humanDateFromId(id) {
  const iso = dateIdToISO(id);
  if (!iso) return id || "—";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function compareMonthYear(aYear, aMonth0, bYear, bMonth0) {
  if (aYear !== bYear) return aYear - bYear;
  return aMonth0 - bMonth0;
}

function parseSaintFeastKey(value) {
  const match = String(value || "").trim().match(/^(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);

  if (!month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  return {
    month,
    day,
    key: `${pad2(month)}-${pad2(day)}`,
  };
}

function getSaintFeastPairs(saint) {
  const keys = Array.isArray(saint?.feastKeys) ? saint.feastKeys : null;
  if (keys && keys.length) {
    return keys.map(parseSaintFeastKey).filter(Boolean);
  }

  if (saint?.feastKey) {
    const parsed = parseSaintFeastKey(saint.feastKey);
    return parsed ? [parsed] : [];
  }

  if (saint?.feastMonth && saint?.feastDay) {
    const month = Number(saint.feastMonth);
    const day = Number(saint.feastDay);

    if (!month || !day) return [];

    return [
      {
        month,
        day,
        key: `${pad2(month)}-${pad2(day)}`,
      },
    ];
  }

  return [];
}

function dateIdToSaintFeastKey(id) {
  const [dd = "", mm = ""] = String(id || "").split("-");
  if (!dd || !mm) return "";
  return `${mm}-${dd}`;
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-dulia-goldSoft">
      {children}
    </div>
  );
}

const COLOR_OPTIONS = [
  { value: "white", dot: "bg-white border border-slate-300", text: "text-slate-900" },
  { value: "red", dot: "bg-red-700", text: "text-white" },
  { value: "green", dot: "bg-green-700", text: "text-white" },
  { value: "violet", dot: "bg-purple-800", text: "text-white" },
  { value: "rose", dot: "bg-pink-400", text: "text-white" },
  { value: "black", dot: "bg-black", text: "text-white" },
  // { value: "gold", dot: "bg-amber-400", text: "text-slate-900" },
];

const linkGold =
    "font-medium text-dulia-navy underline decoration-dulia-gold/60 underline-offset-4 transition hover:decoration-dulia-gold " +
    "dark:text-dulia-goldSoft dark:hover:text-dulia-parchment";

function getColorMeta(color) {
  const match = COLOR_OPTIONS.find((c) => c.value === String(color || "").toLowerCase());
  return match || { value: "", dot: "bg-dulia-navy", text: "text-white" };
}

function celebrationColorClasses(color) {
  const meta = getColorMeta(color);
  return `${meta.dot} ${meta.text}`;
}

function mobileCelebrationCellClasses(color) {
  switch (String(color || "").toLowerCase()) {
    case "white":
      return "bg-white text-slate-900";
    case "red":
      return "bg-red-700 text-white";
    case "green":
      return "bg-green-700 text-white";
    case "violet":
      return "bg-purple-800 text-white";
    case "rose":
      return "bg-pink-400 text-white";
    case "black":
      return "bg-black text-white";
    default:
      return "bg-dulia-navy text-white";
  }
}

function decodeICSValue(value) {
  return String(value || "")
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\")
    .trim();
}

function unfoldICSLines(text) {
  const rawLines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const lines = [];

  for (const line of rawLines) {
    if ((line.startsWith(" ") || line.startsWith("\t")) && lines.length) {
      lines[lines.length - 1] += line.slice(1);
    } else {
      lines.push(line);
    }
  }

  return lines;
}

function parseICSEvents(text) {
  const lines = unfoldICSLines(text);
  const events = [];
  let current = null;

  for (const line of lines) {
    if (line === "BEGIN:VEVENT") {
      current = {};
      continue;
    }

    if (line === "END:VEVENT") {
      if (current) events.push(current);
      current = null;
      continue;
    }

    if (!current) continue;

    const colonIndex = line.indexOf(":");
    if (colonIndex === -1) continue;

    const left = line.slice(0, colonIndex);
    const rawValue = line.slice(colonIndex + 1);
    const [propName] = left.split(";");
    const key = String(propName || "").toUpperCase();

    if (!key) continue;

    const entry = {
      raw: rawValue,
      value: decodeICSValue(rawValue),
    };

    if (!current[key]) current[key] = [];
    current[key].push(entry);
  }

  return events;
}

function getFirstEventProp(event, key) {
  return event?.[key]?.[0]?.value || "";
}

function parseICSDateToDayId(value) {
  const match = String(value || "").match(/^(\d{4})(\d{2})(\d{2})/);
  if (!match) return "";
  const [, yyyy, mm, dd] = match;
  return `${dd}-${mm}-${yyyy}`;
}

function splitImportedCelebrationNames(summary) {
  const text = String(summary || "").trim();
  if (!text) return [];

  return text
    .split(/\s*,\s*or\s+/i)
    .map((part) => part.trim())
    .filter(Boolean);
}

function hexToRgb(hex) {
  const raw = String(hex || "").trim().replace("#", "");
  if (![3, 6].includes(raw.length)) return null;

  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;

  const num = Number.parseInt(full, 16);
  if (Number.isNaN(num)) return null;

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function normalizeImportedColor(rawColor) {
  const value = String(rawColor || "").trim().toLowerCase();

  if (!value) return "green";

  const exact = {
    white: "white",
    red: "red",
    green: "green",
    violet: "violet",
    purple: "violet",
    rose: "rose",
    pink: "rose",
    black: "black",
    gold: "gold",
    yellow: "gold",
    amber: "gold",
  };

  if (exact[value]) return exact[value];

  if (value.includes("violet") || value.includes("purple")) return "violet";
  if (value.includes("rose") || value.includes("pink")) return "rose";
  if (value.includes("green")) return "green";
  if (value.includes("red")) return "red";
  if (value.includes("white")) return "white";
  if (value.includes("black")) return "black";
  if (value.includes("gold") || value.includes("yellow") || value.includes("amber")) {
    return "gold";
  }

  if (/^#?[0-9a-f]{3}$/i.test(value) || /^#?[0-9a-f]{6}$/i.test(value)) {
    const rgb = hexToRgb(value);
    if (!rgb) return "green";

    const palette = {
      white: { r: 245, g: 245, b: 245 },
      red: { r: 185, g: 28, b: 28 },
      green: { r: 21, g: 128, b: 61 },
      violet: { r: 109, g: 40, b: 217 },
      rose: { r: 244, g: 114, b: 182 },
      black: { r: 17, g: 24, b: 39 },
      gold: { r: 245, g: 158, b: 11 },
    };

    let best = "green";
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const [name, sample] of Object.entries(palette)) {
      const distance =
        (rgb.r - sample.r) ** 2 + (rgb.g - sample.g) ** 2 + (rgb.b - sample.b) ** 2;

      if (distance < bestDistance) {
        bestDistance = distance;
        best = name;
      }
    }

    return best;
  }

  return "green";
}

function getImportedRank(event) {
  const rankText = [
    getFirstEventProp(event, "CATEGORIES"),
    getFirstEventProp(event, "CLASS"),
    getFirstEventProp(event, "SUMMARY"),
    getFirstEventProp(event, "DESCRIPTION"),
  ]
    .join(" ")
    .toLowerCase();

  if (rankText.includes("solemnity")) return "solemnity";
  if (rankText.includes("feast")) return "feast";
  return "";
}

function dedupeCelebrations(items) {
  const seen = new Set();
  const out = [];

  for (const item of items) {
    const name = String(item?.name || "").trim();
    const color = String(item?.color || "").trim();
    const key = `${name}__${color}`;
    if (!name || seen.has(key)) continue;
    seen.add(key);
    out.push({ name, color });
  }

  return out;
}

function buildCalendarDaysFromICS(text) {
  const events = parseICSEvents(text);

  if (!events.length) {
    throw new Error("No VEVENT entries were found in this .ics file.");
  }

  const dayMap = new Map();

  for (const event of events) {
    const dayId = parseICSDateToDayId(getFirstEventProp(event, "DTSTART"));
    if (!dayId) continue;

    const summary = getFirstEventProp(event, "SUMMARY");
    const celebrationNames = splitImportedCelebrationNames(summary);
    if (!celebrationNames.length) continue;

    const importedColor = normalizeImportedColor(
      getFirstEventProp(event, "COLOR") || getFirstEventProp(event, "X-APPLE-CALENDAR-COLOR")
    );

    const rank = getImportedRank(event);

    if (!dayMap.has(dayId)) {
      dayMap.set(dayId, {
        date: dayId,
        celebrations: [],
        feast: false,
        solemnity: false,
      });
    }

    const day = dayMap.get(dayId);

    for (const name of celebrationNames) {
      day.celebrations.push({
        name,
        color: importedColor || "green",
      });
    }

    if (rank === "solemnity") {
      day.solemnity = true;
      day.feast = false;
    } else if (rank === "feast" && !day.solemnity) {
      day.feast = true;
    }
  }

  const days = Array.from(dayMap.entries()).map(([id, payload]) => ({
    id,
    payload: {
      ...payload,
      celebrations: dedupeCelebrations(payload.celebrations),
    },
  }));

  if (!days.length) {
    throw new Error("No usable calendar days were found in this .ics file.");
  }

  return days;
}

function RankBadges({ day }) {
  if (!day) return null;

  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {day.solemnity ? (
        <span className="rounded-full bg-dulia-gold/20 px-2.5 py-1 text-[11px] font-semibold text-dulia-night dark:text-dulia-goldSoft">
          Solemnity
        </span>
      ) : null}
      {day.feast ? (
        <span className="rounded-full bg-dulia-navy/10 px-2.5 py-1 text-[11px] font-semibold text-dulia-navy dark:bg-dulia-parchment/10 dark:text-dulia-parchment">
          Feast
        </span>
      ) : null}
    </div>
  );
}

function CelebrationList({ day, compact = false }) {
  const items = Array.isArray(day?.celebrations) ? day.celebrations : [];

  if (!items.length) {
    return (
      <div className="text-sm text-dulia-navy/70 dark:text-dulia-parchment/70">
        No celebrations saved for this day.
      </div>
    );
  }

  return (
    <ul className={compact ? "space-y-2" : "space-y-3"}>
      {items.map((item, idx) => {
        const name = String(item?.name || "").trim();
        const color = String(item?.color || "").trim();

        return (
          <li
            key={`${name}-${color}-${idx}`}
            className="overflow-hidden rounded-xl border border-dulia-line dark:border-dulia-lineStrong/20"
          >
            <div
              className={`px-3 py-2 font-medium ${celebrationColorClasses(color)} ${
                compact ? "text-xs" : "text-sm"
              }`}
            >
              {name || "Untitled"}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function CelebrationStack({ celebrations }) {
  const items = Array.isArray(celebrations) ? celebrations : [];

  if (!items.length) {
    return (
      <div className="mt-2 px-1 text-[10px] text-dulia-navy/35 dark:text-dulia-parchment/30 sm:text-[11px]">
        —
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1 sm:mt-2.5 sm:space-y-1.5">
      {items.map((item, i) => (
        <div
          key={`${item?.name || "item"}-${i}`}
          className={`rounded-md px-2 py-1 text-[10px] font-semibold leading-tight whitespace-normal break-words sm:text-[11px] ${celebrationColorClasses(
            item?.color
          )}`}
          title={item?.name || "Untitled"}
        >
          {item?.name || "Untitled"}
        </div>
      ))}
    </div>
  );
}

function SaintFeastStack({ saints }) {
  const items = Array.isArray(saints) ? saints : [];

  if (!items.length) return null;

  const visible = items.slice(0, 2);
  const remaining = items.length - visible.length;

  return (
    <>
      <div className="mt-1.5 px-1 text-[9px] font-semibold uppercase tracking-[0.08em] text-dulia-navy/55 dark:text-dulia-parchment/50 sm:hidden">
        {items.length} saint feast{items.length === 1 ? "" : "s"}
      </div>

      <div className="mt-2 hidden space-y-1 sm:mt-2.5 sm:block sm:space-y-1.5">
        {visible.map((saint, index) => {
          const name = String(saint?.name || "").trim();

          return (
            <div
              key={`${saint?.id || name || "saint"}-${index}`}
              className="rounded-md border border-dulia-line/70 bg-white/75 px-2 py-1 text-[10px] font-medium leading-tight whitespace-normal break-words text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft dark:text-dulia-parchment/75 sm:text-[11px]"
              title={name || "Untitled"}
            >
              {name || "Untitled"}
            </div>
          );
        })}

        {remaining > 0 ? (
          <div className="px-1 text-[10px] font-medium text-dulia-navy/60 dark:text-dulia-parchment/60 sm:text-[11px]">
            +{remaining} more saint feast{remaining === 1 ? "" : "s"}
          </div>
        ) : null}
      </div>
    </>
  );
}

function SaintFeastList({ saints }) {
  const items = Array.isArray(saints) ? saints : [];

  if (!items.length) return null;

  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-dulia-navy/60 dark:text-dulia-parchment/60">
        Dulia Feast Days
      </div>
      <p className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">These saints have feast days on the selected day, even if they are not listed above as an official celebration on the Liturgical Calendar for the Dioceses of the United States of America.</p>
      <ul className="space-y-2">
        {items.map((saint, index) => {
          const name = String(saint?.name || "").trim();
          const id = String(saint?.id || "").trim();

          return (
            <li
              key={`${id || name || "saint"}-${index}`}
              className="rounded-xl border border-dulia-line/80 bg-dulia-parchment/45 px-3 py-2 text-sm text-dulia-ink dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft dark:text-dulia-parchment"
            >
              {id ? (
                <Link to={`/profile/${id}`} className={linkGold}>
                  {name || "Untitled"}
                </Link>
              ) : (
                name || "Untitled"
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Modal({ open, onClose, title, subtitle, children, rightAction }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="h-[92vh] w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-dulia-night sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-[28px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3 border-b border-dulia-line px-4 py-4 dark:border-dulia-lineStrong/20 sm:flex-row sm:items-start sm:justify-between sm:px-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-1 text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
                {subtitle}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {rightAction}
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className="h-[calc(92vh-86px)] overflow-y-auto px-4 py-4 sm:h-auto sm:max-h-[calc(90vh-88px)] sm:px-5 sm:py-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function ColorSwatchPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {COLOR_OPTIONS.map((option) => {
        const selected = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            title={option.value}
            aria-label={`Select ${option.value}`}
            className={[
              "inline-flex h-10 w-10 items-center justify-center rounded-lg border transition",
              selected
                ? "border-dulia-night ring-2 ring-dulia-gold dark:border-dulia-goldSoft"
                : "border-dulia-line hover:border-dulia-navy/40 dark:border-dulia-lineStrong/20",
            ].join(" ")}
          >
            <span className={`h-5 w-5 rounded-sm ${option.dot}`} />
          </button>
        );
      })}
    </div>
  );
}

function CalendarDayCell({
  cell,
  idx,
  dayObj,
  saintFeasts,
  isViewingCurrentMonth,
  today,
  selectedId,
  onSelect,
}) {
  const cellId = dateIdFromParts(cell.year, cell.month0, cell.day);
  const isToday =
    cell.inMonth &&
    isViewingCurrentMonth &&
    cell.day === today.getDate() &&
    cell.month0 === today.getMonth() &&
    cell.year === today.getFullYear();

  const isSelected = selectedId === cellId;
  const celebrations = Array.isArray(dayObj?.celebrations) ? dayObj.celebrations : [];
  const saintItems = Array.isArray(saintFeasts) ? saintFeasts : [];
  const hasLiturgicalContent = !!dayObj;
  const hasSaintFeasts = saintItems.length > 0;
  const mobileTone =
    cell.inMonth && celebrations.length
      ? mobileCelebrationCellClasses(celebrations[0]?.color)
      : cell.inMonth
      ? "bg-dulia-panel text-dulia-ink dark:bg-dulia-night dark:text-dulia-parchment"
      : "bg-dulia-parchment/55 text-dulia-navy/60 dark:bg-dulia-nightSoft dark:text-dulia-parchment/55";

  return (
    <button
      type="button"
      onClick={() => onSelect(cellId)}
      className={[
        "flex min-h-[88px] flex-col justify-start border-t px-1 py-1 text-left align-top transition sm:min-h-[130px] sm:px-2 sm:py-2 md:min-h-[165px] md:px-2.5 md:py-2.5",
        idx % 7 !== 0 ? "border-l" : "",
        "border-dulia-line dark:border-dulia-lineStrong/20",
        mobileTone,
        cell.inMonth
          ? "sm:bg-dulia-panel sm:hover:bg-dulia-parchment/55 sm:dark:bg-dulia-night sm:dark:hover:bg-dulia-nightSoft"
          : "sm:bg-dulia-parchment/55 sm:text-dulia-navy/60 sm:hover:bg-dulia-parchment/70 sm:dark:bg-dulia-nightSoft sm:dark:text-dulia-parchment/55 sm:dark:hover:bg-dulia-night",
        isToday ? "ring-2 ring-inset ring-dulia-gold/70" : "",
        isSelected ? "ring-2 ring-inset ring-dulia-navy/30 dark:ring-dulia-goldSoft/50" : "",
      ].join(" ")}
    >
      <div className="flex w-full items-start justify-start">
        <div
          className={[
            "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ring-1 ring-black/10 sm:h-7 sm:min-w-7 sm:px-2 sm:text-xs",
            isToday
              ? "bg-dulia-gold text-dulia-night"
              : "bg-white/95 text-slate-900",
          ].join(" ")}
        >
          {cell.day}
        </div>
      </div>

      <div className="mt-0.5 w-full sm:mt-1.5">
        {hasLiturgicalContent || hasSaintFeasts ? (
          <>
            {hasLiturgicalContent ? (
              <>
                <div className="mb-1 flex flex-wrap gap-1 sm:mb-2 sm:gap-1.5">
                  {dayObj.solemnity ? (
                    <span className="rounded-full bg-dulia-goldSoft/95 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-dulia-night ring-1 ring-black/10 sm:px-2 sm:text-[10px]">
                      Solemnity
                    </span>
                  ) : null}
                  {dayObj.feast ? (
                    <span className="rounded-full bg-white/95 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-900 ring-1 ring-black/10 sm:px-2 sm:text-[10px]">
                      Feast
                    </span>
                  ) : null}
                </div>

                <div className="hidden sm:block">
                  <CelebrationStack celebrations={celebrations} />
                </div>
              </>
            ) : null}

            <SaintFeastStack saints={saintItems} />
          </>
        ) : (
          <div className="hidden px-1 text-[10px] text-dulia-navy/35 dark:text-dulia-parchment/30 sm:block sm:text-[11px]">
            —
          </div>
        )}
      </div>
    </button>
  );
}

function emptyCelebration() {
  return { name: "", color: "white" };
}

function buildFormState(id, doc) {
  const celebrations =
    Array.isArray(doc?.celebrations) && doc.celebrations.length
      ? doc.celebrations.map((item) => ({
          name: String(item?.name || ""),
          color: String(item?.color || "white"),
        }))
      : [emptyCelebration()];

  return {
    dateISO: dateIdToISO(id),
    celebrations,
    feast: !!doc?.feast,
    solemnity: !!doc?.solemnity,
  };
}

export default function Calendar() {
  useEffect(() => {
    document.title = "Dulia: Liturgical Calendar";
  }, []);

  const importInputRef = useRef(null);

  const today = useMemo(() => new Date(), []);
  const todayId = useMemo(
    () => dateIdFromParts(today.getFullYear(), today.getMonth(), today.getDate()),
    [today]
  );

  const minSelectableYear = today.getFullYear();
  const minSelectableMonth0 = today.getMonth();
  const maxSelectableYear = 2026;
  const maxSelectableMonth0 = 11; // December

  const minSelectableISO = `${minSelectableYear}-${pad2(minSelectableMonth0 + 1)}-01`;
  const maxSelectableISO = `${maxSelectableYear}-${pad2(maxSelectableMonth0 + 1)}-${pad2(
    daysInMonth(maxSelectableYear, maxSelectableMonth0)
  )}`;

  const [year, setYear] = useState(today.getFullYear());
  const [month0, setMonth0] = useState(today.getMonth());

  const [allDays, setAllDays] = useState([]);
  const [allSaints, setAllSaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [selectedId, setSelectedId] = useState(todayId);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [form, setForm] = useState(buildFormState(todayId, null));
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");

  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [importErr, setImportErr] = useState("");

  const { user, loading: authLoading, admin, adminLoading } = useAuth();
  const gateLoading = authLoading || adminLoading;
  const isAdmin = !!user && !!admin && !gateLoading;

  const selectedRank = form.solemnity ? "solemnity" : form.feast ? "feast" : "";

  const canGoPrev =
    compareMonthYear(year, month0, minSelectableYear, minSelectableMonth0) > 0;
  const canGoNext =
    compareMonthYear(year, month0, maxSelectableYear, maxSelectableMonth0) < 0;

  const heroLink =
    "font-semibold text-dulia-gold underline decoration-dulia-gold/60 underline-offset-4 transition hover:text-dulia-parchment";
  const heroOutlineButtonClass =
    "inline-flex h-10 w-full items-center justify-center rounded-xl border border-white/20 bg-white px-4 text-sm font-semibold text-dulia-ink shadow-sm transition hover:bg-white/95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:rounded-full";
  const heroIconButtonClass =
    "inline-flex h-10 w-full items-center justify-center rounded-xl border border-white/20 bg-white px-4 text-sm font-semibold text-dulia-ink shadow-sm transition hover:bg-white/95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-10 sm:rounded-full sm:px-0";

  function clampVisibleMonthYear(nextYear, nextMonth0) {
    if (
      compareMonthYear(nextYear, nextMonth0, minSelectableYear, minSelectableMonth0) < 0
    ) {
      return { year: minSelectableYear, month0: minSelectableMonth0 };
    }

    if (
      compareMonthYear(nextYear, nextMonth0, maxSelectableYear, maxSelectableMonth0) > 0
    ) {
      return { year: maxSelectableYear, month0: maxSelectableMonth0 };
    }

    return { year: nextYear, month0: nextMonth0 };
  }

  function setVisibleMonthYear(nextYear, nextMonth0) {
    const clamped = clampVisibleMonthYear(nextYear, nextMonth0);
    setYear(clamped.year);
    setMonth0(clamped.month0);
  }

  function setSelectedRank(rank) {
    setForm((prev) => ({
      ...prev,
      feast: rank === "feast",
      solemnity: rank === "solemnity",
    }));
  }

  async function loadCalendarDays() {
    setLoading(true);
    setErr("");
    try {
      const docs = await fetchAllCalendarDays();
      setAllDays(Array.isArray(docs) ? docs : []);
    } catch (e) {
      setErr(e?.message || "Failed to load liturgical calendar days.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCalendarDays();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const docs = await fetchAllSaints();
        setAllSaints(Array.isArray(docs) ? docs : []);
      } catch {
        setAllSaints([]);
      }
    })();
  }, []);

  const byDateId = useMemo(() => {
    const map = new Map();
    for (const d of allDays) {
      map.set(d.id, d);
    }
    return map;
  }, [allDays]);

  const saintFeastsByKey = useMemo(() => {
    const map = new Map();
  
    for (const saint of allSaints) {
      const id = String(saint?.id || "").trim();
      const name = String(saint?.name || "").trim();
      if (!id || !name) continue;
  
      for (const pair of getSaintFeastPairs(saint)) {
        const key = String(pair?.key || "").trim();
        if (!key) continue;
  
        if (!map.has(key)) {
          map.set(key, []);
        }
  
        const items = map.get(key);
        if (!items.some((item) => item?.id === id)) {
          items.push({ id, name });
        }
      }
    }
  
    for (const items of map.values()) {
      items.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    }
  
    return map;
  }, [allSaints]);

  const selectedDay = byDateId.get(selectedId) || null;
  const selectedSaintFeasts = saintFeastsByKey.get(dateIdToSaintFeastKey(selectedId)) || [];

  useEffect(() => {
    setForm(buildFormState(selectedId, selectedDay));
    setFormErr("");
  }, [selectedId, selectedDay]);

  const monthDays = useMemo(() => daysInMonth(year, month0), [year, month0]);
  const firstDow = useMemo(() => startWeekday(year, month0), [year, month0]);

  const isViewingCurrentMonth =
    year === today.getFullYear() && month0 === today.getMonth();

  const gridCells = useMemo(() => {
    const cells = [];

    const prevMonth0 = month0 === 0 ? 11 : month0 - 1;
    const prevYear = month0 === 0 ? year - 1 : year;
    const prevMonthDays = daysInMonth(prevYear, prevMonth0);

    for (let i = 0; i < firstDow; i++) {
      const dayNum = prevMonthDays - (firstDow - 1 - i);
      cells.push({ day: dayNum, inMonth: false, month0: prevMonth0, year: prevYear });
    }

    for (let d = 1; d <= monthDays; d++) {
      cells.push({ day: d, inMonth: true, month0, year });
    }

    const nextMonth0 = month0 === 11 ? 0 : month0 + 1;
    const nextYear = month0 === 11 ? year + 1 : year;

    while (cells.length < 42) {
      const dayNum = cells.length - (firstDow + monthDays) + 1;
      cells.push({ day: dayNum, inMonth: false, month0: nextMonth0, year: nextYear });
    }

    return cells;
  }, [firstDow, monthDays, month0, year]);

  function goPrevMonth() {
    if (!canGoPrev) return;
    const nextDate = new Date(year, month0 - 1, 1);
    setVisibleMonthYear(nextDate.getFullYear(), nextDate.getMonth());
  }

  function goNextMonth() {
    if (!canGoNext) return;
    const nextDate = new Date(year, month0 + 1, 1);
    setVisibleMonthYear(nextDate.getFullYear(), nextDate.getMonth());
  }

  function goToday() {
    const t = new Date();
    const id = dateIdFromParts(t.getFullYear(), t.getMonth(), t.getDate());
    setVisibleMonthYear(t.getFullYear(), t.getMonth());
    setSelectedId(id);
    setDetailsOpen(false);
  }

  function handleSelectDay(id) {
    setSelectedId(id);
    setDetailsOpen(true);
  }

  function openEditForSelected() {
    setForm(buildFormState(selectedId, selectedDay));
    setFormErr("");
    setEditOpen(true);
  }

  function setCelebrationField(index, key, value) {
    setForm((prev) => ({
      ...prev,
      celebrations: prev.celebrations.map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      ),
    }));
  }

  function addCelebrationRow() {
    setForm((prev) => ({
      ...prev,
      celebrations: [...prev.celebrations, emptyCelebration()],
    }));
  }

  function removeCelebrationRow(index) {
    setForm((prev) => {
      const next = prev.celebrations.filter((_, i) => i !== index);
      return {
        ...prev,
        celebrations: next.length ? next : [emptyCelebration()],
      };
    });
  }

  function moveCelebration(index, direction) {
    setForm((prev) => {
      const next = [...prev.celebrations];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, celebrations: next };
    });
  }

  async function handleSave(e) {
    e.preventDefault();

    if (!isAdmin) return;

    setSaving(true);
    setFormErr("");

    try {
      const targetId = isoToDateId(form.dateISO);

      if (!targetId) {
        throw new Error("Please choose a valid date.");
      }

      const targetISO = dateIdToISO(targetId);
      if (targetISO < minSelectableISO || targetISO > maxSelectableISO) {
        throw new Error("Date must be within the allowed range.");
      }

      if (form.feast && form.solemnity) {
        throw new Error("A day cannot be both a feast and a solemnity.");
      }

      const celebrations = (form.celebrations || [])
        .map((item) => ({
          name: String(item?.name || "").trim(),
          color: String(item?.color || "").trim(),
        }))
        .filter((item) => item.name);

      const payload = {
        date: targetId,
        celebrations,
        feast: !!form.feast,
        solemnity: !!form.solemnity,
      };

      const existedAtSelectedId = !!selectedDay;

      if (existedAtSelectedId && targetId !== selectedId) {
        await createSaintDayOrReplace(targetId, payload, selectedId);
      } else if (existedAtSelectedId) {
        await upsertCalendarDay(targetId, payload, { merge: false });
      } else {
        await createCalendarDay(targetId, payload);
      }

      await loadCalendarDays();
      setSelectedId(targetId);

      const [, mm, yyyy] = targetId.split("-").map(Number);
      if (Number.isFinite(mm) && Number.isFinite(yyyy)) {
        setVisibleMonthYear(yyyy, mm - 1);
      }

      setEditOpen(false);
      setDetailsOpen(false);
    } catch (e) {
      setFormErr(e?.message || "Failed to save calendar day.");
    } finally {
      setSaving(false);
    }
  }

  async function createSaintDayOrReplace(targetId, payload, previousId) {
    await createCalendarDay(targetId, payload);
    await deleteCalendarDay(previousId);
  }

  async function handleDelete() {
    if (!isAdmin || !selectedDay) return;

    setSaving(true);
    setFormErr("");

    try {
      await deleteCalendarDay(selectedId);
      await loadCalendarDays();
      setEditOpen(false);
      setDetailsOpen(false);
    } catch (e) {
      setFormErr(e?.message || "Failed to delete calendar day.");
    } finally {
      setSaving(false);
    }
  }

  function triggerICSImport() {
    if (!isAdmin || importing) return;
    setImportMsg("");
    setImportErr("");
    importInputRef.current?.click();
  }

  async function handleICSFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportMsg("");
    setImportErr("");

    try {
      const text = await file.text();
      const importedDays = buildCalendarDaysFromICS(text);

      for (const { id, payload } of importedDays) {
        await upsertCalendarDay(id, payload, { merge: false });
      }

      await loadCalendarDays();

      setImportMsg(
        `Imported ${importedDays.length} calendar day${importedDays.length === 1 ? "" : "s"} from ${file.name}.`
      );
    } catch (err) {
      setImportErr(err?.message || "Failed to import .ics file.");
    } finally {
      setImporting(false);
      if (e.target) e.target.value = "";
    }
  }

  const monthLabel = `${monthName(month0)} ${year}`;

  return (
    <div className="space-y-5 sm:space-y-6">
      <section className="rounded-[24px] bg-dulia-hero p-4 text-white shadow-soft sm:rounded-[30px] sm:p-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <SectionEyebrow>Liturgical Calendar</SectionEyebrow>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
              Calendar
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-white/80 md:text-base">
              Browse the Liturgical Calendar for the Dioceses of the United States of
              America by month. Click{" "}
              <a
                href="https://www.usccb.org/committees/divine-worship/liturgical-calendar"
                className={heroLink}
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>{" "}
              to learn more about the calendar.
            </p>
          </div>

          <input
            ref={importInputRef}
            type="file"
            accept=".ics,text/calendar"
            className="hidden"
            onChange={handleICSFileChange}
          />

          <div className="w-full rounded-[24px] bg-white/10 p-3 backdrop-blur sm:inline-flex sm:w-auto sm:max-w-full sm:rounded-full sm:p-2">
            <div className="grid w-full grid-cols-3 gap-2 sm:flex sm:w-auto sm:flex-nowrap sm:items-center">
              <div className="order-2 w-full sm:order-1 sm:w-auto">
                <button
                  type="button"
                  onClick={goPrevMonth}
                  disabled={!canGoPrev}
                  className={heroIconButtonClass}
                >
                  ←
                </button>
              </div>

              <div className="order-1 col-span-3 min-w-0 sm:order-2 sm:w-[170px] sm:grow-0">
                <Select
                  value={month0 + 1}
                  onChange={(e) => setVisibleMonthYear(year, Number(e.target.value) - 1)}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                    const optionMonth0 = m - 1;
                    const isDisabled =
                      compareMonthYear(year, optionMonth0, minSelectableYear, minSelectableMonth0) <
                        0 ||
                      compareMonthYear(year, optionMonth0, maxSelectableYear, maxSelectableMonth0) >
                        0;

                    return (
                      <option key={m} value={m} disabled={isDisabled}>
                        {monthName(optionMonth0)}
                      </option>
                    );
                  })}
                </Select>
              </div>

              {/* <div className="order-3 sm:order-3">
                <input
                  type="number"
                  inputMode="numeric"
                  min={minSelectableYear}
                  max={maxSelectableYear}
                  value={year}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") return;
                    const n = Number(val);
                    if (Number.isFinite(n)) {
                      setVisibleMonthYear(n, month0);
                    }
                  }}
                  className="h-10 w-full rounded-xl border border-white/20 bg-dulia-night/20 px-3 text-center text-sm font-medium text-white outline-none transition placeholder:text-white/55 focus:border-dulia-gold/50 focus:ring-2 focus:ring-dulia-gold/30 sm:w-[92px]"
                />
              </div> */}

              <div className="order-4 w-full sm:order-4 sm:w-auto">
                <button
                  type="button"
                  onClick={goNextMonth}
                  disabled={!canGoNext}
                  className={heroIconButtonClass}
                >
                  →
                </button>
              </div>

              <div className="order-5 col-span-3 sm:col-span-1 sm:order-5">
                <button
                  type="button"
                  onClick={goToday}
                  disabled={isViewingCurrentMonth && selectedId === todayId}
                  title={
                    isViewingCurrentMonth && selectedId === todayId
                      ? "Already on today"
                      : "Jump to today"
                  }
                  className="inline-flex h-10 w-full items-center justify-center rounded-xl border border-dulia-goldSoft/80 bg-dulia-goldSoft px-4 text-sm font-semibold text-dulia-night transition hover:bg-dulia-goldSoft/90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-dulia-goldSoft dark:bg-dulia-goldSoft dark:text-dulia-night dark:hover:bg-dulia-goldSoft/90 sm:w-auto sm:rounded-full"
                >
                  Today
                </button>
              </div>

              {isAdmin ? (
                <>
                  <div className="order-6 col-span-3 sm:col-span-1 sm:order-6">
                    <button
                      type="button"
                      onClick={openEditForSelected}
                      className={heroOutlineButtonClass}
                    >
                      {selectedDay ? "Edit Selected" : "Create Selected"}
                    </button>
                  </div>

                  {/*
                  <div className="order-7 col-span-3 sm:col-span-1 sm:order-7">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={triggerICSImport}
                      disabled={importing}
                    >
                      {importing ? "Importing…" : "Import .ics"}
                    </Button>
                  </div>
                  */}
                </>
              ) : null}
            </div>
          </div>

          {importMsg ? (
            <div className="rounded-xl border border-emerald-300/40 bg-emerald-50/90 px-3 py-2 text-sm text-emerald-800">
              {importMsg}
            </div>
          ) : null}

          {importErr ? (
            <div className="rounded-xl border border-red-300/40 bg-red-50/90 px-3 py-2 text-sm text-red-800">
              {importErr}
            </div>
          ) : null}
        </div>
      </section>

      {err ? (
        <Card className="rounded-[20px] border-dulia-danger/35 sm:rounded-[24px]">
          <CardBody className="text-sm text-dulia-danger">{err}</CardBody>
        </Card>
      ) : null}

      <Card className="overflow-hidden -mx-4 w-[calc(100%+2rem)] rounded-none sm:mx-0 sm:w-auto sm:rounded-[30px]">
        <div className="overflow-x-auto">
          <div className="min-w-0 sm:min-w-[680px]">
            <div className="grid grid-cols-7 border-b border-dulia-line bg-dulia-parchment/70 text-[10px] font-semibold uppercase tracking-[0.12em] text-dulia-nightSoft dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft dark:text-dulia-parchment/70 sm:text-xs">
              {[
                ["S", "Sun"],
                ["M", "Mon"],
                ["T", "Tue"],
                ["W", "Wed"],
                ["T", "Thu"],
                ["F", "Fri"],
                ["S", "Sat"],
              ].map(([short, full], i) => (
                <div key={`${full}-${i}`} className="px-2 py-2 sm:px-3 sm:py-3">
                  <span className="sm:hidden">{short}</span>
                  <span className="hidden sm:inline">{full}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {gridCells.map((cell, idx) => {
                const cellId = dateIdFromParts(cell.year, cell.month0, cell.day);

                return (
                  <CalendarDayCell
                    key={cellId}
                    cell={cell}
                    idx={idx}
                    dayObj={byDateId.get(cellId) || null}
                    saintFeasts={saintFeastsByKey.get(dateIdToSaintFeastKey(cellId)) || []}
                    isViewingCurrentMonth={isViewingCurrentMonth}
                    today={today}
                    selectedId={selectedId}
                    onSelect={handleSelectDay}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={selectedId === todayId ? "Today" : "Selected Day"}
        subtitle={humanDateFromId(selectedId)}
        rightAction={
          isAdmin ? (
            <Button type="button" variant="outline" onClick={openEditForSelected}>
              {selectedDay ? "Edit Day" : "Create Day"}
            </Button>
          ) : null
        }
      >
        {loading ? (
          <div className="text-dulia-navy/75 dark:text-dulia-parchment/75">Loading…</div>
        ) : selectedDay || selectedSaintFeasts.length ? (
          <div className="space-y-4">
            {selectedDay ? (
              <div>
                <RankBadges day={selectedDay} />
                <CelebrationList day={selectedDay} />
              </div>
            ) : (
              <div className="text-sm text-dulia-navy/70 dark:text-dulia-parchment/70">
                No liturgical celebrations saved for this day.
              </div>
            )}

            <SaintFeastList saints={selectedSaintFeasts} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-dulia-navy/75 dark:text-dulia-parchment/75">
              This date has not been updated yet.
            </div>
            {isAdmin ? (
              <Button type="button" variant="primary" onClick={openEditForSelected}>
                Create Day
              </Button>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={selectedDay ? "Edit Day" : "Create Day"}
        subtitle={humanDateFromId(selectedId)}
      >
        <form className="space-y-4" onSubmit={handleSave}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Date
            </label>
            <Input
              type="date"
              min={minSelectableISO}
              max={maxSelectableISO}
              value={form.dateISO}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, dateISO: e.target.value }))
              }
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Rank
            </label>

            <div className="flex flex-wrap gap-2">
              {[
                { value: "", label: "None" },
                { value: "feast", label: "Feast" },
                { value: "solemnity", label: "Solemnity" },
              ].map((opt) => {
                const active = selectedRank === opt.value;

                return (
                  <button
                    key={opt.value || "none"}
                    type="button"
                    onClick={() => setSelectedRank(opt.value)}
                    className={[
                      "rounded-xl border px-3 py-2 text-sm transition",
                      active
                        ? "border-dulia-gold bg-dulia-gold/15 text-dulia-ink dark:text-dulia-parchment"
                        : "border-dulia-line text-dulia-ink hover:border-dulia-navy/40 dark:border-dulia-lineStrong/20 dark:text-dulia-parchment",
                    ].join(" ")}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
                Celebrations
              </label>
              <Button type="button" variant="outline" onClick={addCelebrationRow}>
                + Add
              </Button>
            </div>

            <div className="space-y-3">
              {form.celebrations.map((item, index) => (
                <div
                  key={index}
                  className="rounded-2xl border border-dulia-line p-3 dark:border-dulia-lineStrong/20"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-dulia-navy/70 dark:text-dulia-parchment/70">
                      Celebration {index + 1}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => moveCelebration(index, -1)}
                        disabled={index === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => moveCelebration(index, 1)}
                        disabled={index === form.celebrations.length - 1}
                      >
                        ↓
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => removeCelebrationRow(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium uppercase tracking-[0.08em] text-dulia-navy/70 dark:text-dulia-parchment/70">
                        Name
                      </label>
                      <Input
                        value={item.name}
                        onChange={(e) =>
                          setCelebrationField(index, "name", e.target.value)
                        }
                        placeholder="Saint Joseph"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-medium uppercase tracking-[0.08em] text-dulia-navy/70 dark:text-dulia-parchment/70">
                        Color
                      </label>
                      <ColorSwatchPicker
                        value={item.color}
                        onChange={(value) => setCelebrationField(index, "color", value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {formErr ? (
            <div className="rounded-xl border border-dulia-danger/35 bg-dulia-danger/5 px-3 py-2 text-sm text-dulia-danger">
              {formErr}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving…" : selectedDay ? "Save Changes" : "Create Day"}
            </Button>

            {selectedDay ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </Button>
            ) : null}
          </div>
        </form>
      </Modal>
    </div>
  );
}